/**
 * Auto-seeded demo fixtures for the production-shape Master Lot Builder.
 * Ten lots in mixed lifecycle states so a stakeholder hitting
 * `/refinery/master-lots` for the first time can immediately:
 *
 *   - see what a fresh draft looks like (ML-2026-D001),
 *   - exercise the inline milestone updater on a mid-flight lot
 *     (ML-2026-D002, sitting at `melted`, with a pending pin-return ETA),
 *   - see what a fully-settled lot looks like with a populated settlement
 *     payload + (no-bytes) stub documents (ML-2026-D003),
 *   - exercise the new "Enter settlement from refiner" flow on a fully
 *     `assayed` lot ready for settlement entry (ML-2026-D004),
 *   - exercise the Metalor-shaped contract on an `assayed` karat-scrap lot
 *     with Pt/Pd opt-in, conditional copper-addition, below-floor Pt, and a
 *     conservative settled ETA seeded from `received` (ML-2026-D005),
 *   - exercise the Saxonia-shaped EUR-currency contract with cross-metal
 *     Ag qualifier + Ag<70% slaModifier + held_on_partner_account
 *     disposition (ML-2026-D006), and
 *   - exercise the Asahi-Canada silver-bullion contract (SR2605539) on an
 *     `assayed` lot that fires the `lotWeightKgMax: 200` slaModifier on
 *     first render (gross ~199.9 kg) AND wires the `hasImpurityCharges`
 *     slaModifier for settlement-entry recompute (ML-2026-D007), and
 *   - exercise the Asahi-USA Au+PGM contract (SR2604523) on an `assayed`
 *     lot with ~1,000 ozt gross + ~5 ozt Pt + ~5 ozt Pd contributions,
 *     surfacing the AC J mixed feeBasis at settlement-entry time — Au
 *     priced per-credited-fine-toz, Ag/Pt/Pd priced per-net-wt-received
 *     via the lot's reported gross (ML-2026-D008), and
 *   - exercise the synthetic multi-metal demo contract (classics + cu) on an
 *     assayed lot so settlement credits a fifth metal without mutating real
 *     PDF term-sheet seeds (ML-2026-D009), and
 *   - ship a confirmed lot with a simulated sales-order number
 *     (ML-2026-D010).
 *
 * These fixtures get loaded on first list-view mount via `bootstrapDemoIfEmpty`
 * (idempotent), or wiped + re-seeded via the admin "Reset demo data" button.
 *
 * Lot codes use the `ML-{year}-D{NNN}` form so they don't collide with the
 * `ML-{year}-{NNNN}` form used for user-created lots.
 *
 * Each manifest unit mirrors the shape served by the real
 * `/refinery/upstream-source-lots` endpoint: `{id, kind, sourceBarId,
 * sourceLotId, sourceLotCode, barNumber, weight: {troyOunces}, purity: {au, ag},
 * assay: {id, type, completedAt}}`. The fake `sourceLotId`s don't resolve
 * against real settled lots — drill-through from these fixtures is expected
 * to break, which is fine for stakeholder demos.
 *
 * THIS FILE IS NOT WIRED TO PRODUCTION DATA. Numbers are believable but
 * fictional and exist only to make the prototype feel concrete.
 */

import cloneDeep from 'lodash/cloneDeep'

import {
  applySettledEta,
  assessContractReadiness,
  buildDemoSalesOrderIdentity,
  DEFAULT_DEMO_NETSUITE_APP_URL,
  buildMasterLotSummarySnapshot,
  computeSettlementPreview,
  computeSettlementTotals,
  resolveMaxSettlementBusinessDays,
  settlementEtaFromReceived,
  summariseManifest,
} from '@/util/refinery/upstream/upstreamMasterLot'
import {normalizeLotInventoryLocation} from '@/util/refinery/upstream/upstreamMasterLot/catalogProductInventory'
import {fixtureSourceUnitSerial} from '@/util/refinery/upstream/upstreamMasterLot/fixtureSourceUnitSerial'
import {
  REFINING_PARTNERS,
  UPSTREAM_REFINING_CONTRACTS,
} from './upstreamMasterLotMockData'

/**
 * Synthetic id of the D002 (mid-flight) fixture's bar #1 body. Used by
 * the deterministic-collision smoke tests in `upstreamMasterLots.spec.js` and
 * `MasterLotCreate.spec.js`, which mock the picker-side BE response with
 * this id and assert it lands on the picker with `allocatedMasterLotId =
 * 'ML-2026-D002'` once the D002 fixture is in vuex state.
 *
 * IMPORTANT: this constant duplicates a string that `makeBarsForLot()`
 * independently derives (`demo-{lotSuffix}-bar-{n}` for `demo-lot-0163`
 * + bar 1). The hard linkage against drift is the spec at
 * `upstreamMasterLotDemoFixtures.spec.js`'s "DEMO_D002_COLLISION_UNIT_ID
 * drift guard" describe block — it asserts the constant matches the
 * specific bar-1 body id emitted by the fixture, so a future rename of
 * the id convention fails CI rather than silently drifting the smoke
 * tests into a false green.
 */
export const DEMO_D002_COLLISION_UNIT_ID = 'demo-0163-bar-1'

const partnerById = (id) => REFINING_PARTNERS.find((partner) => partner.id === id) || null
const contractById = (id) => UPSTREAM_REFINING_CONTRACTS.find((contract) => contract.id === id) || null

// Demo lots predate real auth, so completed milestones are attributed to a
// stand-in UCG ops recorder (mirrors the live behaviour of stamping the
// logged-in user as "recorded by"). Pending milestones carry no recorder yet.
const DEMO_RECORDER = Object.freeze({id: 'demo-user-ucg-ops', name: 'Dana Whitfield'})
const recorderWhenOccurred = (occurredAt) =>
  occurredAt
    ? {recordedByUserId: DEMO_RECORDER.id, recordedByName: DEMO_RECORDER.name}
    : {recordedByUserId: null, recordedByName: null}

const enrichContract = (contract) => {
  if (!contract) return null
  const partner = partnerById(contract.refiningPartnerId)
  return {
    ...cloneDeep(contract),
    partner: partner ? cloneDeep(partner) : null,
  }
}

const makeUnit = ({sourceLotCode, sourceLotId, sourceBarId, barNumber, kind, weightToz, purity, assayType = 'xrf', assayDate = null}) => {
  const serial = fixtureSourceUnitSerial({sourceLotCode, barNumber, kind})
  return {
    id: kind === 'bar' ? sourceBarId : `${sourceBarId}:${kind}`,
    kind,
    sourceBarId,
    sourceLotId,
    sourceLotCode,
    barNumber,
    serialNumber: serial,
    sku: serial,
    weight: {troyOunces: weightToz},
    purity,
    assay: {
      id: `${sourceBarId}-assay`,
      type: assayType,
      completedAt: assayDate ? `${assayDate}T00:00:00.000Z` : null,
    },
  }
}

/**
 * Build a homogenous run of units belonging to a single fake source lot.
 *
 * Each `bar` entry shorthand is `{n, w, kind?, purity?, ...overrides}` where
 * `n` is the bar number and `w` is the weight in troy ounces. `kind` defaults
 * to `'bar'`; pass `'house_pin'` / `'customer_pin'` to emit a pin attached to
 * the same bar number. Per-entry purity overrides whatever default was passed
 * at the helper level so a single `bars: [...]` list can mix gold-bar bodies
 * and 99.9%-pure customer pins without a second function call.
 *
 * The `sourceBarId` is derived from the lot code: `demo-{lot-suffix}-bar-{n}`
 * so multiple kinds of unit hanging off the same physical bar (bar + pin)
 * dedupe by `sourceBarId` while still getting distinct unit `id`s — matching
 * the ID convention emitted by `listUpstreamSourceLots`.
 */
const makeBarsForLot = ({lotCode, lotId, assayType, assayDate, bars, purity}) => {
  const lotSuffix = String(lotId || '').replace(/^demo-lot-/, '')
  return bars.map((bar) => makeUnit({
    sourceLotCode: lotCode,
    sourceLotId: lotId,
    sourceBarId: `demo-${lotSuffix}-bar-${bar.n}`,
    barNumber: bar.n,
    kind: bar.kind || 'bar',
    weightToz: bar.w,
    purity: bar.purity || purity,
    assayType: bar.assayType || assayType,
    assayDate: bar.assayDate || assayDate,
  }))
}

const buildSummary = (sourceUnits, catalogEntries = []) => {
  const all = [...sourceUnits, ...catalogEntries]
  const sourceLotCount = new Set(sourceUnits.map((unit) => unit.sourceLotCode).filter(Boolean)).size
  return buildMasterLotSummarySnapshot({
    allUnits: all,
    sourceUnitCount: sourceUnits.length,
    catalogEntryCount: catalogEntries.length,
    sourceLotCount,
  })
}

const applySettledEtaFromReceived = (masterLot, receivedOccurredAtIso) => {
  const businessDays = resolveMaxSettlementBusinessDays(masterLot.contract, masterLot)
  return applySettledEta(masterLot, settlementEtaFromReceived(receivedOccurredAtIso, businessDays))
}

// Two bars from a fictional RL-2026-0142 — used by the draft demo lot.
const draftSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0142',
  lotId: 'demo-lot-0142',
  assayType: 'xrf',
  assayDate: '2026-04-10',
  bars: [
    {n: 1, w:  82.413, purity: {au: 35.42, ag: null}},
    {n: 2, w: 105.207, purity: {au: 38.34, ag: null}},
  ],
})

const draftTopUpUnit = () => makeUnit({
  sourceLotCode: 'RL-2026-0199',
  sourceLotId: 'source-demo-lot-draft-top-up',
  sourceBarId: 'source-demo-2026-0199-bar-1',
  barNumber: 1,
  kind: 'bar',
  weightToz: 100,
  purity: {au: 99.9, ag: null},
  assayDate: '2026-05-04',
})

// Six bars + two customer pins from fictional RL-2026-0163 — used by the mid-flight demo lot. Pins ride alongside bars as separate units (v1 lot_pin_samples shape).
const midFlightSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0163',
  lotId: 'demo-lot-0163',
  assayType: 'xrf',
  assayDate: '2026-04-20',
  bars: [
    {n: 1, w: 312.605, purity: {au: 99.95, ag: null}},
    {n: 2, w: 198.110, purity: {au: 99.91, ag: null}},
    {n: 3, w: 144.430, purity: {au: 91.66, ag: null}},
    {n: 4, w:  92.812, purity: {au: 75.04, ag: null}},
    {n: 5, w:  61.220, purity: {au: 58.18, ag: null}},
    {n: 6, w:  44.005, purity: {au: 41.55, ag: null}},
    // Customer pins ride alongside their parent bars as separate units; their
    // per-pin assay was run earlier by the partner, so they get their own
    // assay metadata override.
    {n: 1, w:   1.061, kind: 'customer_pin', purity: {au: 99.97, ag: null}, assayType: 'partner', assayDate: '2026-04-15'},
    {n: 2, w:   1.054, kind: 'customer_pin', purity: {au: 99.96, ag: null}, assayType: 'partner', assayDate: '2026-04-15'},
  ],
})

// Five silver bars from a fictional RL-2026-0171 — used by the settled demo
// lot. Big-weight Ag run to demonstrate the IGR logistics-driven minimum.
const settledSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0171',
  lotId: 'demo-lot-0171',
  assayType: 'xrf',
  assayDate: '2026-04-26',
  bars: [
    {n: 1, w: 9842.400, purity: {au: null, ag: 89.92}},
    {n: 2, w: 9755.110, purity: {au: null, ag: 89.88}},
    {n: 3, w: 1208.301, purity: {au: null, ag: 92.31}},
    {n: 4, w: 1432.880, purity: {au: null, ag: 92.45}},
    {n: 5, w: 2105.605, purity: {au: null, ag: 95.04}},
  ],
})

// Demo lot 1 — fresh draft, no milestones advanced. Stakeholder sees
// "what does a brand-new draft look like before we ship it?".
const buildDemoDraftLot = () => {
  const contract = enrichContract(contractById('contract-asahi-au-2026'))
  const sourceUnits = draftSourceUnits()
  return {
    id: 'ML-2026-D001',
    masterLotCode: 'ML-2026-D001',
    status: 'draft',
    contract,
    manifest: {
      sourceUnits,
      catalogEntries: [],
    },
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: null,
      recordedByUserId: null,
      recordedByName: null,
      note: null,
      status: 'pending',
    })),
    notes: 'Initial composition; add the high-Au RL-2026-0199 bar to cross the Au settlement tier.',
    createdAt: '2026-05-05T14:22:00.000Z',
    updatedAt: '2026-05-05T14:22:00.000Z',
  }
}

// Demo lot 2 — mid-flight at `melted`. Three milestones complete with
// realistic timestamps + actor + notes; pin-return milestone has an
// admin-entered ETA but no occurred timestamp; assayed + settled pending.
const buildDemoMidFlightLot = () => {
  const contract = enrichContract(contractById('contract-asahi-au-2026'))
  const sourceUnits = midFlightSourceUnits()
  const milestones = (contract?.expectedMilestones || []).map((milestone) => {
    const base = {
      ...milestone,
      expectedAt: null,
      occurredAt: null,
      recordedByUserId: null,
      recordedByName: null,
      note: null,
      status: 'pending',
    }
    switch (milestone.kind) {
      case 'shipped':
        return {
          ...base,
          occurredAt: '2026-04-25T16:30:00.000Z',
          recordedByUserId: DEMO_RECORDER.id,
          recordedByName: DEMO_RECORDER.name,
          note: 'FedEx 7894-5523-1006, signature required.',
          status: 'complete',
        }
      case 'received':
        return {
          ...base,
          occurredAt: '2026-04-27T09:14:00.000Z',
          recordedByUserId: DEMO_RECORDER.id,
          recordedByName: DEMO_RECORDER.name,
          note: 'Asahi inbound desk confirmed weight match within 0.02 ozt.',
          status: 'complete',
        }
      case 'melted':
        return {
          ...base,
          occurredAt: '2026-05-01T18:45:00.000Z',
          recordedByUserId: DEMO_RECORDER.id,
          recordedByName: DEMO_RECORDER.name,
          note: 'Melt run 26-097, two crucibles.',
          status: 'complete',
        }
      case 'pin_returned':
        return {
          ...base,
          expectedAt: '2026-05-12',
          note: 'Asahi confirmed return shipment scheduled; awaiting tracking.',
        }
      default:
        return base
    }
  })
  return {
    id: 'ML-2026-D002',
    masterLotCode: 'ML-2026-D002',
    status: 'melted',
    contract,
    manifest: {
      sourceUnits,
      catalogEntries: [],
    },
    summary: buildSummary(sourceUnits),
    milestones,
    notes: 'Held back from RL-2026-0163 settle for partner top-up.',
    createdAt: '2026-04-22T13:00:00.000Z',
    updatedAt: '2026-05-01T18:45:00.000Z',
  }
}

// Demo lot 3 — fully settled. Settlement carries the cash-based payload and two stub documents (no IndexedDB blob behind them) so the read-side card's "doc registered without bytes" path is demoable without bundling sample PDFs.
const buildDemoSettledLot = () => {
  const contract = enrichContract(contractById('contract-igr-ag-2026'))
  const sourceUnits = settledSourceUnits()
  const milestoneTimes = {
    shipped: '2026-03-18T15:00:00.000Z',
    received: '2026-03-22T11:30:00.000Z',
    melted: '2026-03-29T20:10:00.000Z',
    assayed: '2026-04-04T13:25:00.000Z',
    settled: '2026-04-09T17:00:00.000Z',
  }
  const milestones = (contract?.expectedMilestones || []).map((milestone) => ({
    ...milestone,
    expectedAt: null,
    occurredAt: milestoneTimes[milestone.kind] || null,
    ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
    note: null,
    status: 'complete',
  }))
  // Reported gross is a few toz off our manifest gross (24344.296) so the
  // read-side card's variance column has something interesting to render.
  const reportedGrossToz = 24344.296 + 7.5
  // Derive the fixture payload through the same pure settlement helpers used
  // by the dialog/mock API so the demo statement cannot drift from the math.
  const perMetal = computeSettlementPreview(contract, summariseManifest(sourceUnits), {
    reportedGrossToz,
    perMetal: {
      // Ag at the >=20k toz tier: 98% accountability, $0.03/fine-toz refining fee.
      // metalValueUsd is transcribed from partner statement at ~$30/toz spot.
      ag: {purityPct: 91.10, metalValueUsd: 652000.00},
      // Trace Au (0.05% purity) uses IGR's flat incidental-Au tier.
      au: {purityPct: 0.05, metalValueUsd: 30000.00},
    },
  })
  const contractFees = {
    perMetalAssayFee: {ag: {feeUsd: 175}, au: {feeUsd: 175}},
    otherFees: [{code: 'treatment', kind: 'flat', flatUsd: 150, included: true}],
  }
  const adHocFees = [
    {id: 'demo-fee-igr-freight', label: 'Inbound freight & insurance', amountUsd: 2400.00},
  ]
  const totals = computeSettlementTotals({
    perMetal,
    contractFees,
    adHocFees,
    reportedGrossToz,
  })
  return {
    id: 'ML-2026-D003',
    masterLotCode: 'ML-2026-D003',
    status: 'settled',
    contract,
    manifest: {
      sourceUnits,
      catalogEntries: [],
    },
    summary: buildSummary(sourceUnits),
    milestones,
    settlement: {
      recordedAt: '2026-04-09T17:00:00.000Z',
      partnerReferenceNumber: 'IGR-2604-0188',
      reportedGrossToz,
      perMetal,
      contractFees,
      adHocFees,
      ...totals,
      notes: 'Logistics-driven minimum hit (~25k ozt Ag) before shipment. ' +
             'Partner re-weigh +7.5 ozt over our manifest gross.',
      documents: [
        // Stub records — no blob bytes behind them. Demonstrates the
        // "doc registered without retrievable bytes" path on the read-side
        // card (the lazy accessor returns null, the Open link disables).
        {
          id: 'demo-doc-igr-statement-cover',
          filename: 'IGR-2604-0188-statement.pdf',
          mimeType: 'application/pdf',
          byteSize: 187392,
          caption: 'Settlement statement cover',
          uploadedAt: '2026-04-09T17:00:00.000Z',
        },
        {
          id: 'demo-doc-igr-bar-photos',
          filename: 'IGR-2604-0188-bar-photos.jpg',
          mimeType: 'image/jpeg',
          byteSize: 1482511,
          caption: 'Bar photos at receipt',
          uploadedAt: '2026-04-09T17:01:00.000Z',
        },
      ],
    },
    notes: 'Logistics-driven minimum hit (~25k ozt Ag) before shipment.',
    // Sample audit trail so the read-side `MasterLotContractHistoryCard`
    // has demo coverage out-of-the-box. Two entries — first a partner-side
    // switch from an earlier Asahi-only contract, then a re-snapshot to
    // pick up an in-flight tier-table edit by ops.
    contractHistory: [
      {
        at: '2026-03-18T14:00:00.000Z',
        kind: 'switch',
        fromContractId: 'contract-asahi-au-2026',
        fromContractTitle: 'Asahi Au — 2026 terms',
        toContractId: 'contract-igr-ag-2026',
        toContractTitle: 'IGR Ag/Au — 2026 terms',
        notes: 'Switched to IGR after Ag content confirmed >25k ozt minimum',
      },
      {
        at: '2026-03-22T09:30:00.000Z',
        kind: 're-snapshot',
        fromContractId: 'contract-igr-ag-2026',
        fromContractTitle: 'IGR Ag/Au — 2026 terms',
        toContractId: 'contract-igr-ag-2026',
        toContractTitle: 'IGR Ag/Au — 2026 terms',
        notes: 'Pulled latest IGR rate-tier correction',
      },
    ],
    createdAt: '2026-03-15T10:00:00.000Z',
    updatedAt: '2026-04-09T17:00:00.000Z',
  }
}

// Four bars from a fictional RL-2026-0188 — used by the assayed demo lot
// (D004). Comfortably above the 500 toz Asahi minimum (~600 toz Au across
// mixed-purity karated jewelry). All milestones complete through `assayed`
// so a stakeholder lands on the lot, sees the "Enter settlement from
// refiner" CTA, and runs the full settlement-entry flow end-to-end.
const assayedSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0188',
  lotId: 'demo-lot-0188',
  assayType: 'xrf',
  assayDate: '2026-05-02',
  bars: [
    {n: 1, w: 198.215, purity: {au: 99.92, ag: 0.10}},
    {n: 2, w: 152.640, purity: {au: 99.85, ag: null}},
    {n: 3, w: 142.310, purity: {au: 91.42, ag: null}},
    {n: 4, w: 109.870, purity: {au: 75.06, ag: null}},
  ],
})

const confirmedSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0210',
  lotId: 'demo-lot-0210',
  assayType: 'xrf',
  assayDate: '2026-06-01',
  bars: [
    {n: 1, w: 198.215, purity: {au: 99.92, ag: 0.10}},
    {n: 2, w: 152.640, purity: {au: 99.85, ag: null}},
    {n: 3, w: 142.310, purity: {au: 91.42, ag: null}},
    {n: 4, w: 109.870, purity: {au: 75.06, ag: null}},
  ],
})

// Demo lot 4 — fully `assayed`, settlement still pending. Stakeholders use
// this lot to exercise the new settlement-entry flow without touching the
// other fixtures.
const buildDemoAssayedLot = () => {
  const contract = enrichContract(contractById('contract-asahi-au-2026'))
  const sourceUnits = assayedSourceUnits()
  const milestoneTimes = {
    shipped: '2026-04-22T15:00:00.000Z',
    received: '2026-04-25T10:30:00.000Z',
    melted: '2026-04-29T19:45:00.000Z',
    pin_returned: '2026-05-01T14:00:00.000Z',
    assayed: '2026-05-02T16:30:00.000Z',
  }
  const milestones = (contract?.expectedMilestones || []).map((milestone) => ({
    ...milestone,
    expectedAt: null,
    occurredAt: milestoneTimes[milestone.kind] || null,
    ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
    note: null,
    status: milestoneTimes[milestone.kind] ? 'complete' : 'pending',
  }))
  return {
    id: 'ML-2026-D004',
    masterLotCode: 'ML-2026-D004',
    status: 'assayed',
    contract,
    manifest: {
      sourceUnits,
      catalogEntries: [],
    },
    summary: buildSummary(sourceUnits),
    milestones,
    notes: 'Awaiting settlement statement from Asahi (expected 2026-05-12).',
    createdAt: '2026-04-19T11:30:00.000Z',
    updatedAt: '2026-05-02T16:30:00.000Z',
  }
}

// Karat-medium-scrap run for the Metalor demo lot (D005). Mixed Au/Ag with
// trace Pt/Pd so purity-content tiering + below-floor warnings are exercisable.
const metalorSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0195',
  lotId: 'demo-lot-0195',
  assayType: 'xrf',
  assayDate: '2026-05-06',
  bars: [
    {n: 1, w: 48.320, purity: {au: 58.42, ag: 11.80, pt: 0.09, pd: 0.06}},
    {n: 2, w: 52.105, purity: {au: 71.55, ag: 9.25, pt: 0.11, pd: 0.08}},
    {n: 3, w: 41.880, purity: {au: 44.10, ag: 14.60, pt: 0.07, pd: 0.05}},
    {n: 4, w: 38.640, purity: {au: 82.30, ag: 6.40, pt: 0.12, pd: 0.09}},
  ],
})

// Demo lot 5 — Metalor karat scrap, `assayed`, settlement pending. Exercises
// purityPct tiers, fee-waived Au/Ag, Pt/Pd assay opt-in, conditional copper
// addition, below-minimum-return Pt at typical statement purity, and a
// conservative settled ETA (30bd) seeded when `received` was logged.
const buildDemoMetalorAssayedLot = () => {
  const contract = enrichContract(contractById('contract-metalor-karat-medium-scrap-2026'))
  const sourceUnits = metalorSourceUnits()
  const receivedAt = '2026-05-08T10:15:00.000Z'
  const milestoneTimes = {
    shipped: '2026-05-05T16:00:00.000Z',
    received: receivedAt,
    melted: '2026-05-09T19:30:00.000Z',
    assayed: '2026-05-10T14:45:00.000Z',
  }
  let lot = {
    id: 'ML-2026-D005',
    masterLotCode: 'ML-2026-D005',
    status: 'assayed',
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: milestoneTimes[milestone.kind] || null,
      ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
      note: milestone.kind === 'received'
        ? 'Metalor inbound scale ticket MT-2605-4412.'
        : null,
      status: milestoneTimes[milestone.kind] ? 'complete' : 'pending',
    })),
    notes:
      'Karat medium scrap — await partner statement. Expect Pt below return floor ' +
      'unless recovery improves; opt in to Pt/Pd assay at settlement.',
    createdAt: '2026-05-04T09:00:00.000Z',
    updatedAt: '2026-05-10T14:45:00.000Z',
  }
  lot = applySettledEtaFromReceived(lot, receivedAt)
  return lot
}

// Five bars from a fictional RL-2026-0207 — used by the Saxonia demo
// lot (D006). ~1000 toz total at ~65% Ag fineness so the manifest lands
// in the Saxonia Ag `[50.0, 70.0)` band (98.0% accountability) AND fires
// the `Ag < 70% +10bd` slaModifier. Trace Au + Pt + Pd content exercises
// cross-metal qualification (Au/Pt/Pd rows qualify on Ag fineness)
// end-to-end against a non-trivial 4-metal manifest.
const saxoniaSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0207',
  lotId: 'demo-lot-0207',
  assayType: 'xrf',
  assayDate: '2026-04-18',
  bars: [
    {n: 1, w: 200.150, purity: {ag: 70.20, au: 0.85, pt: 0.05, pd: 0.04}},
    {n: 2, w: 200.310, purity: {ag: 60.10, au: 1.02, pt: 0.06, pd: 0.05}},
    {n: 3, w: 199.980, purity: {ag: 65.40, au: 1.20, pt: 0.04, pd: 0.03}},
    {n: 4, w: 200.220, purity: {ag: 68.15, au: 0.92, pt: 0.05, pd: 0.06}},
    {n: 5, w: 200.110, purity: {ag: 62.05, au: 1.08, pt: 0.07, pd: 0.05}},
  ],
})

// Demo lot 6 — Saxonia bullion, `settled` with the off-system disposition
// modality so the no-cash flow renders on the read-side. Exercises:
//   - EUR currency tag (chip on accountability card)
//   - cross-metal tier qualifier (Ag-qualified Au/Pt/Pd rows)
//   - Ag<70% slaModifier (manifest lands at ~65% Ag fineness)
//   - feeWaived metalRates across all four metals
//   - dispositionMode: 'held_on_partner_account' (no USD invoice, off-system credit)
//   - adHocFees row for the EUR refining-charge USD-conversion
//     transcription (metalRates credits-only; fee via adHocFees).
const buildDemoSaxoniaSettledLot = () => {
  const contract = enrichContract(contractById('contract-saxonia-bullion-2026'))
  const sourceUnits = saxoniaSourceUnits()
  const milestoneTimes = {
    shipped: '2026-04-15T16:00:00.000Z',
    received: '2026-04-22T09:30:00.000Z',
    melted: '2026-04-28T18:20:00.000Z',
    assayed: '2026-05-04T13:10:00.000Z',
    settled: '2026-05-21T11:00:00.000Z',
  }
  const milestones = (contract?.expectedMilestones || []).map((milestone) => ({
    ...milestone,
    expectedAt: null,
    occurredAt: milestoneTimes[milestone.kind] || null,
    ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
    note: milestone.kind === 'received'
      ? 'Saxonia inbound receipt confirmed; SLA anchor prototyped via `received` (real contract anchors on "after written release").'
      : null,
    status: 'complete',
  }))
  const summary = buildSummary(sourceUnits)
  const reportedGrossToz = summary.grossWeightToz
  // Settlement preview is computed from the SAME pure helpers used by the
  // dialog/mock API so the demo statement cannot drift from the math. With
  // every metalRates row at feeWaived, every refiningFeeUsd is $0 — the
  // partner-billed EUR refining charge surfaces in adHocFees below.
  const perMetal = computeSettlementPreview(contract, summariseManifest(sourceUnits), {
    reportedGrossToz,
    perMetal: {
      ag: {purityPct: summary.weightedAvgPurityAg, metalValueUsd: 19500.00},
      au: {purityPct: summary.weightedAvgPurityAu, metalValueUsd: 24300.00},
      pt: {purityPct: summary.weightedAvgPurityPt, metalValueUsd: 480.00},
      pd: {purityPct: summary.weightedAvgPurityPd, metalValueUsd: 410.00},
    },
  })
  const contractFees = {
    perMetalAssayFee: {
      ag: {feeUsd: 0},
      au: {feeUsd: 0},
      pt: {feeUsd: 0},
      pd: {feeUsd: 0},
    },
    otherFees: [],
  }
  // Saxonia partner-billed refining at €/kg credited metal — transcribed
  // here as a USD-converted single adHocFees row. Real-world flow: the
  // operator computes the per-metal EUR sub-totals from
  // `referenceFeeSchedule.refiningCosts` against the partner's released
  // assay numbers, sums them, and uses the partner's posted EUR→USD rate.
  const adHocFees = [
    {
      id: 'demo-fee-saxonia-refining',
      label: 'Saxonia refining (€/kg credited → USD)',
      amountUsd: 5140.00,
      code: 'other',
    },
  ]
  const totals = computeSettlementTotals({
    perMetal,
    contractFees,
    adHocFees,
    reportedGrossToz,
  })
  return {
    id: 'ML-2026-D006',
    masterLotCode: 'ML-2026-D006',
    status: 'settled',
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary,
    milestones,
    settlement: {
      recordedAt: '2026-05-21T11:00:00.000Z',
      partnerReferenceNumber: 'SAX-26-04-1812',
      reportedGrossToz,
      perMetal,
      contractFees,
      adHocFees,
      ...totals,
      // Non-cash disposition keeps credited metal off-system.
      dispositionMode: 'held_on_partner_account',
      dispositionDetails: {
        destination: 'Saxonia partner-side metals credit account',
        note: 'Net credited metal stays on Saxonia\'s books for future redemption / shipment; no USD invoice issued.',
      },
      notes:
        'Manifest Ag fineness ~65% triggered the Ag<70% slaModifier (+10 bd on the per-tier 25 bd base). ' +
        'EUR refining charge transcribed from partner statement at the posted EUR→USD rate. ' +
        'Disposition: balance held on Saxonia partner account; redemption decision pending.',
      documents: [
        {
          id: 'demo-doc-saxonia-statement',
          filename: 'SAX-26-04-1812-abrechnung.pdf',
          mimeType: 'application/pdf',
          byteSize: 245120,
          caption: 'Saxonia Abrechnung (EUR primary statement)',
          uploadedAt: '2026-05-21T11:00:00.000Z',
        },
        {
          id: 'demo-doc-saxonia-statement-en',
          filename: 'SAX-26-04-1812-statement-en.pdf',
          mimeType: 'application/pdf',
          byteSize: 218770,
          caption: 'Saxonia statement (English/USD secondary)',
          uploadedAt: '2026-05-21T11:01:00.000Z',
        },
      ],
    },
    notes:
      'Saxonia bullion demo lot — exercises EUR currency, cross-metal Ag qualifier ' +
      '(Au/Pt/Pd ride the Ag-qualified SLA), Ag<70% slaModifier, off-system credit disposition, ' +
      'and EUR refining-charge USD-conversion via adHocFees.',
    createdAt: '2026-04-12T13:00:00.000Z',
    updatedAt: '2026-05-21T11:00:00.000Z',
  }
}

// Five Ag-bullion bars from a fictional RL-2026-0221 — used by D007.
// Gross ~199.9 kg sits intentionally under the contract's 10000-toz
// hard minimum so the `lotWeightKgMax: 200` slaModifier fires on first render;
// the lot's `notes` field below surfaces the violation to UI readers.
// Structured readiness evaluates the source-unit requirement independently.
const asahiSilverSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0221',
  lotId: 'demo-lot-0221',
  assayType: 'xrf',
  assayDate: '2026-05-22',
  bars: [
    {n: 1, w: 1285.000, purity: {ag: 94.15, au: 0.08}},
    {n: 2, w: 1286.500, purity: {ag: 93.88, au: 0.07}},
    {n: 3, w: 1284.200, purity: {ag: 94.42, au: 0.09}},
    {n: 4, w: 1287.000, purity: {ag: 93.92, au: 0.06}},
    {n: 5, w: 1283.800, purity: {ag: 94.05, au: 0.08}},
  ],
})

// Demo lot 7 — Asahi-Canada silver-bullion `assayed` lot per real SR2605539
// contract. Exercises:
//   - 88-99.9% Ag fineness ladder (manifest at ~94% → `[90, 99.9)` band:
//     98.75% accountability, 45 bd per-tier SLA)
//   - cross-metal Au row qualified on Ag fineness (Au pays $1.55/fine-toz)
//   - **lotWeightKgMax: 200 slaModifier fires** (gross ~199.9 kg) → +10 bd
//     on top of the 45 bd per-tier SLA → settled-ETA chip reflects 55 bd
//     base, surfacing via applySettledEtaFromReceived
//   - **hasImpurityCharges: true slaModifier wired** through the contract —
//     fires when operator transcribes an `impurity_charge` adHocFees row at
//     settlement entry (manual validation step 11 per plan; +10 bd more →
//     total 65 bd post-transcription)
//   - Lot is **intentionally below the contract's hard 10,000-toz lot minimum**
//     floor — a realistic 10,000+ toz lot is 311+ kg and would NOT exercise
//     the weight-based modifier. Documented in the lot's `notes` so
//     reviewers don't confuse the violation for a fixture bug.
const buildDemoAsahiSilverAssayedLot = () => {
  const contract = enrichContract(contractById('contract-asahi-ag-2026'))
  const sourceUnits = asahiSilverSourceUnits()
  const receivedAt = '2026-05-08T11:15:00.000Z'
  const milestoneTimes = {
    shipped: '2026-05-02T14:30:00.000Z',
    received: receivedAt,
    melted: '2026-05-15T18:00:00.000Z',
    assayed: '2026-05-22T16:45:00.000Z',
  }
  let lot = {
    id: 'ML-2026-D007',
    masterLotCode: 'ML-2026-D007',
    status: 'assayed',
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: milestoneTimes[milestone.kind] || null,
      ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
      note: milestone.kind === 'received'
        ? 'Asahi SLC inbound receipt confirmed; silver bullion contract billed by Asahi Canada Ltd.'
        : null,
      status: milestoneTimes[milestone.kind] ? 'complete' : 'pending',
    })),
    notes:
      'Asahi-silver demo lot (real SR2605539 contract). Manifest ~94% Ag, ~6,426 ozt / ' +
      '~199.9 kg gross. **Intentionally below the contract\'s 10,000-ozt minimum** to ' +
      'exercise the `Lot < 200 kg → +10 bd` weight-based slaModifier — real production ' +
      'lots will be 10,000+ ozt and 311+ kg. Settled ETA reflects 55 bd from receipt ' +
      '(45 bd per-tier + 10 bd weight modifier). The +10 bd `hasImpurityCharges` ' +
      'modifier wires through the contract — fires the moment an operator transcribes ' +
      'an `impurity_charge`-coded adHocFees row at settlement entry, recomputing the ' +
      'ETA to 65 bd from receipt. Exercises the dual-entity (Asahi Canada billing / ' +
      'Asahi USA SLC facility) workflow.',
    createdAt: '2026-04-28T10:00:00.000Z',
    updatedAt: '2026-05-22T16:45:00.000Z',
  }
  lot = applySettledEtaFromReceived(lot, receivedAt)
  return lot
}

// Four mixed-purity karat bars from fictional RL-2026-0234 — used by D008. Au-heavy with Ag/Pt/Pd sized so the manifest carries ~5 ozt Pt + ~5 ozt Pd against ~1,000 ozt gross, making the cross-qualified per-metal metalValueUsd lines believable alongside the net-wt refining math (which multiplies against lot grossToz regardless of per-metal contribution).
const asahiGoldFullShapeSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0234',
  lotId: 'demo-lot-0234',
  assayType: 'xrf',
  assayDate: '2026-05-22',
  bars: [
    {n: 1, w: 260.105, purity: {au: 85.40, ag: 8.20, pt: 0.55, pd: 0.50}},
    {n: 2, w: 248.330, purity: {au: 88.15, ag: 6.55, pt: 0.60, pd: 0.45}},
    {n: 3, w: 252.880, purity: {au: 82.90, ag: 9.10, pt: 0.50, pd: 0.55}},
    {n: 4, w: 238.605, purity: {au: 87.60, ag: 7.30, pt: 0.50, pd: 0.50}},
  ],
})

// Demo lot 8 — Asahi-USA Au+PGM full-shape assayed lot per SR2604523. Exercises: purityPct tierDimension + tierQualifyingMetal: 'au'; Au own-qualified [50,99.5) band (99.80%, fee-waived, 10bd SLA); Ag/Pt/Pd cross-qualified on Au with netWeightReceivedToz refining basis (the only paid-metals fixture exercising net-wt refining); referenceFeeSchedule.metalThresholds on Ag/Pt/Pd; Pt/Pd 60bd dominates contract-wide max → 60bd settled-ETA from receipt. Manifest ~1,000 ozt with ~5 ozt Pt + ~5 ozt Pd.
const buildDemoAsahiGoldFullShapeAssayedLot = () => {
  const contract = enrichContract(contractById('contract-asahi-au-2026'))
  const sourceUnits = asahiGoldFullShapeSourceUnits()
  const receivedAt = '2026-05-12T09:45:00.000Z'
  const milestoneTimes = {
    shipped: '2026-05-08T15:15:00.000Z',
    received: receivedAt,
    melted: '2026-05-18T19:10:00.000Z',
    pin_returned: '2026-05-20T13:30:00.000Z',
    assayed: '2026-05-22T17:00:00.000Z',
  }
  let lot = {
    id: 'ML-2026-D008',
    masterLotCode: 'ML-2026-D008',
    status: 'assayed',
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: milestoneTimes[milestone.kind] || null,
      ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
      note: milestone.kind === 'received'
        ? 'Asahi SLC inbound receipt confirmed; Au+PGM contract billed by Asahi Refining USA, Inc.'
        : null,
      status: milestoneTimes[milestone.kind] ? 'complete' : 'pending',
    })),
    notes:
      'Asahi-gold full-shape demo lot (real SR2604523 contract). Manifest ~86% Au, ~1,000 ozt ' +
      'gross with ~5 ozt Pt + ~5 ozt Pd content. Au own-qualified at `[50, 99.5)` band — ' +
      '99.80% accountability, fee waived, 10 bd per-tier SLA. Ag/Pt/Pd cross-qualified on ' +
      'Au fineness via AC G — refining math fires against reportedGrossToz (NOT per-metal ' +
      'contribution) via AC J\'s netWeightReceivedToz basis at settlement entry. Settled ' +
      'ETA reflects 60 bd from receipt (Pt/Pd dominate the per-tier max). Exercises the ' +
      'dual-entity (Asahi USA billing / SLC facility) workflow.',
    createdAt: '2026-05-04T08:30:00.000Z',
    updatedAt: '2026-05-22T17:00:00.000Z',
  }
  lot = applySettledEtaFromReceived(lot, receivedAt)
  return lot
}

// @experimental multi-metal demo units — classics + cu for D009 settlement credit path.
const multiMetalDemoSourceUnits = () => makeBarsForLot({
  lotCode: 'RL-2026-0299',
  lotId: 'demo-lot-0299',
  assayType: 'xrf',
  assayDate: '2026-05-28',
  bars: [
    {n: 1, w: 260.105, purity: {au: 85.40, ag: 8.20, pt: 0.55, pd: 0.50, cu: 3.80}},
    {n: 2, w: 248.330, purity: {au: 88.15, ag: 6.55, pt: 0.60, pd: 0.45, cu: 3.20}},
    {n: 3, w: 252.880, purity: {au: 82.90, ag: 9.10, pt: 0.50, pd: 0.55, cu: 4.10}},
    {n: 4, w: 238.605, purity: {au: 87.60, ag: 7.30, pt: 0.50, pd: 0.50, cu: 3.50}},
  ],
})

// Demo lot 9 — synthetic multi-metal contract (NOT a real PDF). Proves >4 paid metals incl. cu credited at settlement without mutating Asahi/Metalor/Saxonia seeds.
const buildDemoMultiMetalAssayedLot = () => {
  const contract = enrichContract(contractById('contract-demo-multi-metal-2026'))
  const sourceUnits = multiMetalDemoSourceUnits()
  const receivedAt = '2026-05-20T10:00:00.000Z'
  const milestoneTimes = {
    shipped: '2026-05-16T14:00:00.000Z',
    received: receivedAt,
    melted: '2026-05-24T16:00:00.000Z',
    pin_returned: '2026-05-26T12:00:00.000Z',
    assayed: '2026-05-28T18:00:00.000Z',
  }
  let lot = {
    id: 'ML-2026-D009',
    masterLotCode: 'ML-2026-D009',
    status: 'assayed',
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: milestoneTimes[milestone.kind] || null,
      ...recorderWhenOccurred(milestoneTimes[milestone.kind] || null),
      note: milestone.kind === 'received'
        ? 'Demo inbound receipt — synthetic multi-metal contract.'
        : null,
      status: milestoneTimes[milestone.kind] ? 'complete' : 'pending',
    })),
    notes:
      'Synthetic multi-metal demo lot (contract-demo-multi-metal-2026). Manifest carries ' +
      'positive Cu alongside Au/Ag/Pt/Pd so settlement preview credits a fifth paid metal. ' +
      'Not a live partner lot — use for change-contract / settlement POC only.',
    createdAt: '2026-05-12T09:00:00.000Z',
    updatedAt: '2026-05-28T18:00:00.000Z',
  }
  lot = applySettledEtaFromReceived(lot, receivedAt)
  return lot
}

// @experimental confirmed + SO fixture for the ship/fulfill walkthrough.
const buildDemoConfirmedLot = () => {
  const contract = enrichContract(contractById('contract-asahi-au-2026'))
  const sourceUnits = confirmedSourceUnits()
  const readiness = assessContractReadiness(sourceUnits, contract)
  return {
    id: 'ML-2026-D010',
    masterLotCode: 'ML-2026-D010',
    status: 'confirmed',
    readinessWarnings: readiness.warnings,
    contract,
    manifest: {sourceUnits, catalogEntries: []},
    summary: buildSummary(sourceUnits),
    milestones: (contract?.expectedMilestones || []).map((milestone) => ({
      ...milestone,
      expectedAt: null,
      occurredAt: null,
      recordedByUserId: null,
      recordedByName: null,
      note: null,
      status: 'pending',
    })),
    notes: 'Confirmed demo lot ready for simulated sales-order fulfillment.',
    createdAt: '2026-06-01T09:00:00.000Z',
    updatedAt: '2026-06-01T10:00:00.000Z',
  }
}

export const buildDemoMasterLotFixtures = ({
  netsuiteAppUrl = DEFAULT_DEMO_NETSUITE_APP_URL,
} = {}) => [
  buildDemoDraftLot(),
  buildDemoMidFlightLot(),
  buildDemoSettledLot(),
  buildDemoAssayedLot(),
  buildDemoMetalorAssayedLot(),
  buildDemoSaxoniaSettledLot(),
  buildDemoAsahiSilverAssayedLot(),
  buildDemoAsahiGoldFullShapeAssayedLot(),
  buildDemoMultiMetalAssayedLot(),
  buildDemoConfirmedLot(),
].map((lot) => ({
  ...lot,
  inventoryLocation: normalizeLotInventoryLocation(lot.inventoryLocation),
  salesOrder: lot.status === 'draft'
    ? null
    : buildDemoSalesOrderIdentity(lot.masterLotCode, netsuiteAppUrl),
}))

export const __testing__ = {
  buildDemoDraftLot,
  buildDemoMidFlightLot,
  buildDemoSettledLot,
  buildDemoAssayedLot,
  buildDemoMetalorAssayedLot,
  buildDemoSaxoniaSettledLot,
  buildDemoAsahiSilverAssayedLot,
  buildDemoAsahiGoldFullShapeAssayedLot,
  buildDemoMultiMetalAssayedLot,
  buildDemoConfirmedLot,
  draftTopUpUnit,
  buildSummary,
  applySettledEtaFromReceived,
}
